import { normalizeChartTimeframe } from "../core/chart-timeframes.js";
import { getIndicatorDefinition, defaultInputs } from "../indicators/indicator-definitions.js";
import { createIndicatorInstance, validateInput } from "../indicators/indicator-model.js";
import customizationRegistry from "../../../CommonModels/CustomizationRegistry.json";

export const EFFECTIVE_CUSTOMIZATION_SCHEMA_VERSION = 1;

export const THEME_COLOR_KEYS = Object.freeze([
  "applicationBackground",
  "applicationSurface",
  "elevatedSurface",
  "toolbarBackground",
  "panelBackground",
  "border",
  "borderStrong",
  "interactiveHover",
  "primaryText",
  "secondaryText",
  "mutedText",
  "primaryAction",
  "accent",
  "focusRing",
  "positive",
  "negative",
  "warning",
  "chartBackground",
  "chartGrid",
  "chartAxis",
  "chartText",
  "chartMutedText",
  "crosshair",
  "selection",
  "candleUpBody",
  "candleUpBorder",
  "candleUpWick",
  "candleDownBody",
  "candleDownBorder",
  "candleDownWick",
  "volumeUp",
  "volumeDown",
  "drawingDefault",
  "drawingSelection"
]);

const FEATURE_PROPERTY_BY_ID = Object.freeze({
  "object-tree": "objectTree",
  "indicator-manager": "indicatorManager",
  "drawing-toolbar": "drawingToolbar",
  alerts: "alerts",
  watchlists: "watchlists",
  templates: "templates",
  "chart-settings": "chartSettings",
  "go-to-date": "goToDate",
  clock: "clock",
  account: "account",
  "market-data": "marketData"
});

export const FEATURE_KEYS = Object.freeze(
  customizationRegistry.features.map((key) => FEATURE_PROPERTY_BY_ID[key])
);

export const TOOLBAR_REGIONS = Object.freeze([...customizationRegistry.toolbarRegions]);

const TOOLBAR_COMMAND_SELECTORS = Object.freeze({
  account: ".chart-account-menu-shell, .chart-toolbar-top > .chart-brand",
  "symbol-search": ".chart-symbol-control",
  "new-chart": "[data-new-chart]",
  watchlists: "[data-new-watchlist]",
  alerts: "[data-open-alert-manager]",
  "object-tree": "[data-open-object-tree]",
  "market-state": ".chart-market-state",
  "focus-chart": '[data-viewport-action="focus"]',
  drawings: "[data-line-tools]",
  "zoom-in": '[data-viewport-action="zoom-in"]',
  "zoom-out": '[data-viewport-action="zoom-out"]',
  "reset-view": '[data-reset-view]',
  indicators: "[data-open-indicator-manager]",
  "reset-layout": "[data-window-reset]",
  "theme-toggle": "[data-theme-toggle]",
  fullscreen: "[data-fullscreen-toggle]",
  "market-data-status": ".chart-status-source",
  "workspace-status": ".chart-status-hint",
  "render-status": ".chart-status-metrics",
  clock: "[data-toolbar-clock]"
});

// Region, feature, capability, and mandatory metadata comes from the same
// source-controlled registry used by backend publication validation. Only the
// selector that binds a command to known application markup is client-owned.
export const TOOLBAR_COMMANDS = Object.freeze(Object.fromEntries(
  customizationRegistry.commands.map((command) => [command.id, Object.freeze({
    regions: Object.freeze([...command.regions]),
    mandatoryIn: Object.freeze([...(command.mandatoryIn || [])]),
    feature: command.feature || null,
    capability: command.capability || null,
    selector: TOOLBAR_COMMAND_SELECTORS[command.id] || "[data-no-such-command]"
  })])
));

const REGION_SELECTORS = Object.freeze({
  top: ".chart-toolbar-top",
  left: ".chart-toolrail-left",
  right: ".chart-toolrail-right",
  bottom: ".chart-statusbar"
});

const FEATURE_SELECTORS = Object.freeze({
  objectTree: ["[data-open-object-tree]", "[data-object-tree-window]"],
  indicatorManager: ["[data-open-indicator-manager]", "[data-indicator-manager]", '[data-chart-context-action="indicators"]'],
  drawingToolbar: ["[data-line-tools]", '[data-chart-context-action="drawings"]'],
  alerts: [
    "[data-open-alert-manager]",
    '[data-chart-context-action="add-alert"]',
    '[data-indicator-action="alert"]',
    '[data-drawing-context-action="alert"]'
  ],
  watchlists: ["[data-new-watchlist]"],
  templates: ['[data-template-trigger="chart"]', '[data-template-trigger="indicator"]'],
  chartSettings: ['[data-chart-context-action="settings"]'],
  goToDate: ['[data-chart-context-action="go-to-date"]', "[data-go-to-date-layer]"],
  clock: ["[data-toolbar-clock]"],
  account: ["[data-account-menu-shell]"],
  marketData: [".chart-status-source", "[data-market-data-management]"]
});

const CSS_VARIABLES = Object.freeze({
  applicationBackground: "--chart-app-bg",
  applicationSurface: "--chart-control",
  elevatedSurface: "--chart-panel-raised",
  toolbarBackground: "--chart-toolbar",
  panelBackground: "--chart-panel",
  border: "--chart-border",
  borderStrong: "--chart-border-strong",
  interactiveHover: "--chart-control-hover",
  primaryText: "--chart-text",
  secondaryText: "--chart-muted",
  mutedText: "--chart-subtle",
  primaryAction: "--chart-primary-action",
  accent: "--chart-cyan",
  focusRing: "--chart-focus",
  positive: "--chart-up",
  negative: "--chart-down",
  warning: "--chart-warning",
  chartText: "--chart-canvas-text",
  chartMutedText: "--chart-canvas-muted",
  selection: "--chart-selection",
  drawingDefault: "--chart-drawing",
  drawingSelection: "--chart-drawing-selection"
});

const SAFE_FONT_FAMILIES = Object.freeze({
  system: "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
  inter: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
  "roboto-mono": "Roboto Mono, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace"
});

export class EffectiveCustomizationError extends Error {
  constructor(message) {
    super(message);
    this.name = "EffectiveCustomizationError";
  }
}

export function validateEffectiveCustomization(value) {
  const customization = requiredRecord(value, "customization");
  onlyKeys(customization, [
    "schemaVersion",
    "effectiveHash",
    "educatorRevision",
    "productRevision",
    "branding",
    "themes",
    "chartDefaults",
    "defaultIndicators",
    "initialWorkspace",
    "toolbar",
    "features"
  ], "customization");
  if (customization.schemaVersion !== EFFECTIVE_CUSTOMIZATION_SCHEMA_VERSION) {
    throw new EffectiveCustomizationError(
      `Unsupported effective customization schema version ${String(customization.schemaVersion)}.`
    );
  }
  requiredHash(customization.effectiveHash, "customization.effectiveHash");
  optionalRevision(customization.educatorRevision, "customization.educatorRevision");
  optionalRevision(customization.productRevision, "customization.productRevision");

  const branding = requiredRecord(customization.branding, "customization.branding");
  onlyKeys(branding, [
    "displayName",
    "shortName",
    "logoUrl",
    "compactLogoUrl",
    "faviconUrl",
    "primaryColor",
    "accentColor",
    "fontFamilyKey"
  ], "customization.branding");
  requiredString(branding.displayName, "customization.branding.displayName");
  requiredString(branding.shortName, "customization.branding.shortName");
  requiredColor(branding.primaryColor, "customization.branding.primaryColor");
  requiredColor(branding.accentColor, "customization.branding.accentColor");
  if (!Object.hasOwn(SAFE_FONT_FAMILIES, branding.fontFamilyKey)) {
    throw new EffectiveCustomizationError("customization.branding.fontFamilyKey is unsupported.");
  }
  optionalAssetUrl(branding.logoUrl, "customization.branding.logoUrl");
  optionalAssetUrl(branding.compactLogoUrl, "customization.branding.compactLogoUrl");
  optionalAssetUrl(branding.faviconUrl, "customization.branding.faviconUrl");

  const themes = requiredRecord(customization.themes, "customization.themes");
  onlyKeys(themes, ["light", "dark"], "customization.themes");
  const light = validateTheme(themes.light, "customization.themes.light");
  const dark = validateTheme(themes.dark, "customization.themes.dark");
  const chartDefaults = validateChartDefaults(customization.chartDefaults);
  const defaultIndicators = validateIndicators(
    customization.defaultIndicators,
    "customization.defaultIndicators"
  );
  const initialWorkspace = validateInitialWorkspace(customization.initialWorkspace);
  const toolbar = validateToolbars(customization.toolbar);
  const features = validateFeatures(customization.features);

  return deepFreeze({
    ...customization,
    branding: { ...branding },
    themes: { light, dark },
    chartDefaults,
    defaultIndicators,
    initialWorkspace,
    toolbar,
    features
  });
}

export function applyEffectiveTheme(
  documentObject,
  customization,
  requestedMode = customization?.chartDefaults?.themeMode
) {
  const mode = requestedMode === "light" ? "light" : "dark";
  const palette = customization.themes[mode];
  const style = documentObject?.documentElement?.style;
  if (!style?.setProperty) {
    throw new EffectiveCustomizationError("A document root is required to apply chart theming.");
  }
  documentObject.documentElement.dataset.theme = mode;
  for (const [key, property] of Object.entries(CSS_VARIABLES)) {
    style.setProperty(property, palette[key]);
  }
  // --chart-bg is the historical DOM surface token. Canvas/WebGL consume
  // chartBackground directly through their typed palette instead.
  style.setProperty("--chart-bg", palette.applicationBackground);
  style.setProperty("--chart-toolbar", palette.toolbarBackground);
  style.setProperty("--chart-sma", palette.indicatorPalette[0]);
  style.setProperty(
    "--chart-ema",
    palette.indicatorPalette[1] || palette.indicatorPalette[0]
  );
  style.setProperty("--chart-font", SAFE_FONT_FAMILIES[customization.branding.fontFamilyKey]);
  style.setProperty("--chart-brand-primary", customization.branding.primaryColor);
  style.setProperty("--chart-brand-accent", customization.branding.accentColor);
  style.fontFamily = SAFE_FONT_FAMILIES[customization.branding.fontFamilyKey];
  return palette;
}

export function resolveEffectiveThemeMode(mode, matchMedia = globalThis.matchMedia) {
  if (mode === "system") {
    return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: light)")?.matches
      ? "light"
      : "dark";
  }
  return mode === "light" ? "light" : "dark";
}

export function paletteChartSettings(customization, mode) {
  const palette = customization.themes[mode === "light" ? "light" : "dark"];
  return {
    backgroundStyle: "solid",
    backgroundColor: opaqueHex(palette.chartBackground),
    backgroundColorEnd: opaqueHex(palette.chartBackground),
    horizontalGridColor: opaqueHex(palette.chartGrid),
    verticalGridColor: opaqueHex(palette.chartGrid),
    crosshairColor: opaqueHex(palette.crosshair),
    bodyUpColor: opaqueHex(palette.candleUpBody),
    bodyDownColor: opaqueHex(palette.candleDownBody),
    borderUpColor: opaqueHex(palette.candleUpBorder),
    borderDownColor: opaqueHex(palette.candleDownBorder),
    wickUpColor: opaqueHex(palette.candleUpWick),
    wickDownColor: opaqueHex(palette.candleDownWick),
    chartType: customization.chartDefaults.chartType,
    sessionType: customization.chartDefaults.session,
    crosshairStyle: customization.chartDefaults.crosshairMode === "off"
      ? "hidden"
      : customization.chartDefaults.crosshairMode === "full" ? "dashed" : "solid"
  };
}

export function customizationIndicatorInstances(definitions, indicatorPalette = []) {
  return validateIndicators(definitions, "defaultIndicators").map((configured, index) => {
    const definition = getIndicatorDefinition(configured.definitionKey);
    const instance = createIndicatorInstance(definition, {
      id: `configured-${index + 1}-${definition.id}`,
      paneId: configured.pane === "new-pane" ||
        (configured.pane === "auto" && definition.defaultPane !== "main")
        ? `pane-${index + 2}`
        : "main",
      visible: configured.visible,
      inputs: configured.parameters,
      styles: configured.styles
    });
    const colorValue = indicatorPalette[index % Math.max(1, indicatorPalette.length)] || null;
    const color = colorValue ? colorValue.slice(0, 7) : null;
    const styles = color
      ? Object.fromEntries(Object.entries(instance.styles).map(([key, value]) => [
          key,
          { ...value, color }
        ]))
      : instance.styles;
    return { ...instance, order: index, styles };
  });
}

export function initialChartSeeds(customization, maximumCharts, themeMode = null) {
  const limit = Math.max(0, Math.min(
    Number.isInteger(maximumCharts) ? maximumCharts : 0,
    customization.initialWorkspace.chartCount
  ));
  const base = customization.chartDefaults;
  const charts = customization.initialWorkspace.charts;
  const paletteMode = themeMode === "light" ? "light" : "dark";
  const indicatorPalette = customization.themes[paletteMode].indicatorPalette;
  return Array.from({ length: limit }, (_, index) => {
    const chart = charts[index] || {};
    return {
      symbol: chart.symbol || base.symbol,
      symbolId: chart.symbolId || base.symbolId || null,
      timeframe: chart.timeframe || base.timeframe,
      chartType: chart.chartType || base.chartType,
      session: chart.session || base.session,
      indicators: customizationIndicatorInstances(
        chart.indicators || customization.defaultIndicators,
        indicatorPalette
      )
    };
  });
}

export function applyCustomizationPresentation(documentObject, customization) {
  for (const region of TOOLBAR_REGIONS) {
    applyToolbarRegion(documentObject, region, customization.toolbar[region]);
  }

  const applicationRoot = documentObject.querySelector?.("[data-chart-app]");
  for (const key of FEATURE_KEYS) {
    const visible = customization.features[key];
    applicationRoot?.setAttribute?.(`data-feature-${camelToKebab(key)}`, String(visible));
    for (const selector of FEATURE_SELECTORS[key]) {
      for (const element of documentObject.querySelectorAll(selector)) {
        // A visible feature does not override a toolbar manifest that omitted
        // the command. A hidden feature always removes its ordinary entry
        // points, including dynamically-created context actions through the
        // root data attribute and CSS rule.
        if (!visible) element.hidden = true;
        element.dataset.customizationFeature = visible ? "visible" : "hidden";
      }
    }
  }
}

export function applyBranding(documentObject, branding) {
  for (const image of documentObject.querySelectorAll(".chart-brand-lockup img")) {
    if (branding.logoUrl) image.src = branding.logoUrl;
    image.alt = "";
  }
  for (const image of documentObject.querySelectorAll(".chart-brand-icon")) {
    if (branding.compactLogoUrl || branding.logoUrl) {
      image.src = branding.compactLogoUrl || branding.logoUrl;
    }
    image.alt = "";
  }
  for (const brand of documentObject.querySelectorAll(".chart-brand")) {
    brand.dataset.brandDisplayName = branding.displayName;
    // The account trigger owns an action label used by assistive technology;
    // educator display text must not replace that control's accessible name.
    if (brand.matches?.("[data-account-menu-trigger]")) continue;
    brand.setAttribute("aria-label", branding.displayName);
  }
  if (branding.faviconUrl && documentObject.head) {
    let favicon = documentObject.head.querySelector('link[rel~="icon"]');
    if (!favicon) {
      favicon = documentObject.createElement("link");
      favicon.rel = "icon";
      documentObject.head.append(favicon);
    }
    favicon.href = branding.faviconUrl;
  }
}

export function featureIsVisible(customization, key) {
  return FEATURE_KEYS.includes(key) && customization?.features?.[key] === true;
}

function validateTheme(value, path) {
  const palette = requiredRecord(value, path);
  onlyKeys(palette, [...THEME_COLOR_KEYS, "indicatorPalette"], path);
  const normalized = {};
  for (const key of THEME_COLOR_KEYS) {
    normalized[key] = requiredColor(palette[key], `${path}.${key}`);
  }
  if (!Array.isArray(palette.indicatorPalette) ||
      palette.indicatorPalette.length < 1 ||
      palette.indicatorPalette.length > 12) {
    throw new EffectiveCustomizationError(
      `${path}.indicatorPalette must contain between 1 and 12 colors.`
    );
  }
  normalized.indicatorPalette = palette.indicatorPalette.map((color, index) =>
    requiredColor(color, `${path}.indicatorPalette[${index}]`));
  return normalized;
}

function validateChartDefaults(value) {
  const defaults = requiredRecord(value, "customization.chartDefaults");
  onlyKeys(defaults, [
    "symbol",
    "symbolId",
    "timeframe",
    "themeMode",
    "chartType",
    "session",
    "volumeVisible",
    "crosshairMode"
  ], "customization.chartDefaults");
  requiredString(defaults.symbol, "customization.chartDefaults.symbol");
  optionalGuid(defaults.symbolId, "customization.chartDefaults.symbolId");
  const timeframe = normalizeChartTimeframe(defaults.timeframe);
  if (!timeframe) {
    throw new EffectiveCustomizationError("customization.chartDefaults.timeframe is unsupported.");
  }
  oneOf(defaults.themeMode, customizationRegistry.themeModes, "customization.chartDefaults.themeMode");
  oneOf(defaults.chartType, customizationRegistry.chartTypes, "customization.chartDefaults.chartType");
  oneOf(defaults.session, customizationRegistry.sessions, "customization.chartDefaults.session");
  oneOf(defaults.crosshairMode, customizationRegistry.crosshairModes, "customization.chartDefaults.crosshairMode");
  if (typeof defaults.volumeVisible !== "boolean") {
    throw new EffectiveCustomizationError("customization.chartDefaults.volumeVisible must be a boolean.");
  }
  return { ...defaults, symbol: defaults.symbol.trim().toUpperCase(), timeframe };
}

function validateIndicators(value, path) {
  if (!Array.isArray(value) || value.length > 24) {
    throw new EffectiveCustomizationError(`${path} must be an array containing at most 24 indicators.`);
  }
  return value.map((item, index) => {
    const currentPath = `${path}[${index}]`;
    const indicator = requiredRecord(item, currentPath);
    onlyKeys(indicator, ["definitionKey", "parameters", "visible", "pane"], currentPath);
    const definition = getIndicatorDefinition(indicator.definitionKey);
    if (!definition) {
      throw new EffectiveCustomizationError(`${currentPath}.definitionKey is unsupported.`);
    }
    if (typeof indicator.visible !== "boolean") {
      throw new EffectiveCustomizationError(`${currentPath}.visible must be a boolean.`);
    }
    oneOf(indicator.pane, customizationRegistry.panePlacements, `${currentPath}.pane`);
    const parameters = requiredRecord(indicator.parameters, `${currentPath}.parameters`);
    const defaults = defaultInputs(definition);
    for (const key of Object.keys(parameters)) {
      if (!Object.hasOwn(defaults, key)) {
        throw new EffectiveCustomizationError(`${currentPath}.parameters.${key} is unsupported.`);
      }
    }
    const normalizedParameters = { ...defaults };
    for (const input of definition.inputs) {
      if (!Object.hasOwn(parameters, input.key)) continue;
      const result = validateInput(definition, input.key, parameters[input.key], {
        ...normalizedParameters,
        ...parameters
      });
      if (!result.valid) {
        throw new EffectiveCustomizationError(
          `${currentPath}.parameters.${input.key}: ${result.message}`
        );
      }
      normalizedParameters[input.key] = result.value;
    }
    return {
      ...indicator,
      definitionKey: definition.id,
      parameters: Object.fromEntries(
        Object.keys(parameters).map((key) => [key, normalizedParameters[key]])
      )
    };
  });
}

function validateInitialWorkspace(value) {
  const workspace = requiredRecord(value, "customization.initialWorkspace");
  onlyKeys(workspace, [
    "layoutPreset",
    "chartCount",
    "activeChartIndex",
    "charts"
  ], "customization.initialWorkspace");
  oneOf(workspace.layoutPreset, customizationRegistry.layoutPresets, "customization.initialWorkspace.layoutPreset");
  if (!Number.isInteger(workspace.chartCount) || workspace.chartCount < 0 || workspace.chartCount > 4) {
    throw new EffectiveCustomizationError("customization.initialWorkspace.chartCount must be between 0 and 4.");
  }
  if (!Number.isInteger(workspace.activeChartIndex) || workspace.activeChartIndex < 0 ||
      (workspace.chartCount === 0 && workspace.activeChartIndex !== 0) ||
      (workspace.chartCount > 0 && workspace.activeChartIndex >= workspace.chartCount)) {
    throw new EffectiveCustomizationError("customization.initialWorkspace.activeChartIndex is invalid.");
  }
  if (!Array.isArray(workspace.charts) || workspace.charts.length > workspace.chartCount) {
    throw new EffectiveCustomizationError("customization.initialWorkspace.charts exceeds chartCount.");
  }
  const charts = workspace.charts.map((chartValue, index) => {
    const path = `customization.initialWorkspace.charts[${index}]`;
    const chart = requiredRecord(chartValue, path);
    onlyKeys(chart, ["symbol", "symbolId", "timeframe", "chartType", "indicators"], path);
    requiredString(chart.symbol, `${path}.symbol`);
    optionalGuid(chart.symbolId, `${path}.symbolId`);
    if (!normalizeChartTimeframe(chart.timeframe)) {
      throw new EffectiveCustomizationError(`${path}.timeframe is unsupported.`);
    }
    oneOf(chart.chartType, customizationRegistry.chartTypes, `${path}.chartType`);
    const indicators = validateIndicators(chart.indicators, `${path}.indicators`);
    return {
      ...chart,
      symbol: chart.symbol.trim().toUpperCase(),
      timeframe: normalizeChartTimeframe(chart.timeframe),
      indicators
    };
  });
  return { ...workspace, charts };
}

function validateToolbars(value) {
  const toolbars = requiredRecord(value, "customization.toolbar");
  onlyKeys(toolbars, TOOLBAR_REGIONS, "customization.toolbar");
  return Object.fromEntries(TOOLBAR_REGIONS.map((region) => {
    const entries = toolbars[region];
    if (!Array.isArray(entries) || entries.length > 32) {
      throw new EffectiveCustomizationError(`customization.toolbar.${region} must be an array.`);
    }
    const seen = new Set();
    const normalized = entries.map((entryValue, index) => {
      const path = `customization.toolbar.${region}[${index}]`;
      const entry = requiredRecord(entryValue, path);
      onlyKeys(entry, ["kind", "command"], path);
      oneOf(entry.kind, ["command", "separator"], `${path}.kind`);
      if (entry.kind === "separator") {
        if (entry.command !== null && entry.command !== undefined) {
          throw new EffectiveCustomizationError(`${path}.command is not allowed on a separator.`);
        }
        if (index === 0 || index === entries.length - 1 || entries[index - 1]?.kind === "separator") {
          throw new EffectiveCustomizationError(`${path} is not a structurally valid separator.`);
        }
        return { kind: "separator" };
      }
      requiredString(entry.command, `${path}.command`);
      const registryCommand = customizationRegistry.commands.find(
        (candidate) => candidate.id === entry.command
      );
      if (!registryCommand || !registryCommand.regions.includes(region)) {
        throw new EffectiveCustomizationError(`${path}.command is unsupported in ${region}.`);
      }
      if (seen.has(entry.command)) {
        throw new EffectiveCustomizationError(`${path}.command is duplicated.`);
      }
      seen.add(entry.command);
      return { kind: "command", command: entry.command };
    });
    for (const command of customizationRegistry.commands) {
      if (command.mandatoryIn?.includes(region) && !seen.has(command.id)) {
        normalized.push({ kind: "command", command: command.id });
      }
    }
    return [region, normalized];
  }));
}

function validateFeatures(value) {
  const features = requiredRecord(value, "customization.features");
  onlyKeys(features, FEATURE_KEYS, "customization.features");
  const normalized = {};
  for (const key of FEATURE_KEYS) {
    if (typeof features[key] !== "boolean") {
      throw new EffectiveCustomizationError(`customization.features.${key} must be a boolean.`);
    }
    normalized[key] = features[key];
  }
  return normalized;
}

function applyToolbarRegion(documentObject, region, entries) {
  const container = documentObject.querySelector(REGION_SELECTORS[region]);
  if (!container) return;
  for (const separator of container.querySelectorAll("[data-customization-separator]")) {
    separator.remove();
  }
  for (const structural of container.querySelectorAll(
    ":scope > .chart-rail-separator, :scope > .chart-rail-spacer"
  )) {
    structural.hidden = true;
  }
  const configured = new Set(entries
    .filter((entry) => entry.kind === "command")
    .map((entry) => entry.command));
  for (const [commandId, command] of Object.entries(TOOLBAR_COMMANDS)) {
    if (!command.regions.includes(region)) continue;
    for (const element of container.querySelectorAll(command.selector)) {
      const hidden = !configured.has(commandId) && !command.mandatoryIn.includes(region);
      element.hidden = hidden;
      element.dataset.customizationToolbar = hidden ? "hidden" : "visible";
    }
  }
  for (const entry of entries) {
    if (entry.kind === "separator") {
      const separator = documentObject.createElement?.("span");
      if (separator) {
        separator.dataset.customizationSeparator = "";
        separator.className = region === "left" || region === "right"
          ? "chart-rail-separator"
          : "chart-customization-separator";
        separator.setAttribute("role", "separator");
        separator.setAttribute("aria-orientation", region === "left" || region === "right"
          ? "horizontal"
          : "vertical");
        container.append(separator);
      }
      continue;
    }
    const command = TOOLBAR_COMMANDS[entry.command];
    const element = container.querySelector(command?.selector || "[data-no-such-command]");
    if (element && element.parentElement === container) container.append(element);
  }
}

function onlyKeys(value, allowed, path) {
  const allowedKeys = new Set(allowed);
  const unknown = Object.keys(value).find((key) => !allowedKeys.has(key));
  if (unknown) {
    throw new EffectiveCustomizationError(`${path}.${unknown} is unsupported.`);
  }
}

function camelToKebab(value) {
  return String(value).replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

function requiredRecord(value, path) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new EffectiveCustomizationError(`${path} must be an object.`);
  }
  return value;
}

function requiredString(value, path) {
  if (typeof value !== "string" || value.trim().length === 0 || /[\u0000-\u001f]/.test(value)) {
    throw new EffectiveCustomizationError(`${path} must be a non-empty safe string.`);
  }
  return value.trim();
}

function requiredColor(value, path) {
  if (typeof value !== "string" || !/^#[0-9a-f]{6}(?:[0-9a-f]{2})?$/i.test(value)) {
    throw new EffectiveCustomizationError(`${path} must be a normalized hexadecimal color.`);
  }
  return value.toUpperCase();
}

function requiredHash(value, path) {
  if (typeof value !== "string" || !/^[0-9a-f]{64}$/i.test(value)) {
    throw new EffectiveCustomizationError(`${path} must be a SHA-256 content hash.`);
  }
}

function optionalRevision(value, path) {
  if (value !== null && value !== undefined && (!Number.isInteger(value) || value < 1)) {
    throw new EffectiveCustomizationError(`${path} must be a positive revision number or null.`);
  }
}

function optionalGuid(value, path) {
  if (value === null || value === undefined) return;
  if (typeof value !== "string" || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
    throw new EffectiveCustomizationError(`${path} must be a GUID or null.`);
  }
}

function optionalAssetUrl(value, path) {
  if (value === null || value === undefined) return;
  if (typeof value !== "string" || !value.startsWith("/tenant-assets/") ||
      value.startsWith("//") || value.includes("\\") || value.includes("..") ||
      /^(?:https?|data|javascript|file):/i.test(value)) {
    throw new EffectiveCustomizationError(`${path} must be a safe tenant asset URL or null.`);
  }
}

function oneOf(value, choices, path) {
  if (!choices.includes(value)) {
    throw new EffectiveCustomizationError(`${path} is unsupported.`);
  }
}

function opaqueHex(value) {
  return String(value).slice(0, 7).toUpperCase();
}

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.freeze(value);
  for (const nested of Object.values(value)) deepFreeze(nested);
  return value;
}
